import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthStore } from '@/store/auth.store';
import { useCurrentAttestationText } from '@/api/hooks/useAttestations';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

// Batch 2 — ONE combined screen: template details + the IP attestation, ONE confirm → ONE
// ATOMIC call (POST /assessment-templates with the attestation fields inline). The server
// creates the template AND records a fresh linked ip_upload attestation in one tx (both-or-
// neither). ⚠ Success ("recorded") is shown ONLY after the atomic commit; on any error nothing
// is created (rollback) and no "recorded" claim is made; cancel writes nothing.
export function CustomAuthoringOnboardingPage() {
  const navigate = useNavigate();
  const { tenantId } = useAuthStore();
  const queryClient = useQueryClient();

  useEffect(() => { document.title = 'Create Custom Template — VeriGRC'; }, []);

  const { data: textVersion, isLoading: loadingText, refetch: refetchText } = useCurrentAttestationText('ip_upload_attestation');

  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [sourceDescription, setSourceDescription] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [stale, setStale] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    templateName.trim().length > 0 &&
    sourceDescription.trim().length > 0 &&
    accepted &&
    !!textVersion &&
    !loadingText &&
    !submitting;

  async function handleConfirm() {
    if (!canSubmit || !textVersion) return;
    setSubmitting(true);
    setStale(false);
    try {
      // ONE atomic call — template + fresh linked ip_upload attestation, both-or-neither.
      const { data } = await apiClient.post<{ data: { id: string } }>('/assessment-templates', {
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        attestation_text_version_id: textVersion.id,
        source_description: sourceDescription.trim(),
      });
      const newId = data.data.id;
      queryClient.invalidateQueries({ queryKey: ['assessment-templates', tenantId] });
      // "recorded" is claimed ONLY here — after the atomic commit returned success.
      toast.success(`Template "${templateName.trim()}" created and your attestation recorded.`);
      navigate(`/templates/${newId}/edit`);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { errors?: { code?: string } } } };
      const code = e?.response?.data?.errors?.code;
      // Any failure = the whole tx rolled back: no template, no attestation, nothing recorded.
      if (code === 'ATTESTATION_TEXT_VERSION_STALE') {
        setStale(true);
        setAccepted(false);
        refetchText();
        toast.error('The IP terms have been updated. Please re-read and re-accept.');
      } else {
        toast.error('Failed to create template. Nothing was created — please try again.');
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-text-primary">Create Custom Template</h1>
        <p className="text-sm text-text-secondary mt-1">
          Name your template and confirm your organisation&rsquo;s right to use its content. The template is created
          and your attestation recorded together — in one step.
        </p>
      </div>

      {/* Template details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Template Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="template-name" className="text-sm">
              Template name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="template-name"
              placeholder="e.g. 'Vendor Security Assessment Q4 2026'"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              disabled={submitting}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="template-desc" className="text-sm">
              Description <span className="text-text-muted">(optional)</span>
            </Label>
            <Textarea
              id="template-desc"
              placeholder="Brief description of this template's purpose and target vendor type"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              rows={3}
              disabled={submitting}
            />
          </div>
        </CardContent>
      </Card>

      {/* IP attestation */}
      <Card className="border-border-subtle shadow-sm">
        <CardHeader className="pb-4 pt-5 px-6">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-cyan/10 border border-accent-cyan/20">
              <ShieldCheck className="h-5 w-5 text-accent-cyan" />
            </div>
            <div>
              <CardTitle className="text-base">Content IP (Intellectual Property) Attestation</CardTitle>
              <p className="text-sm text-text-secondary mt-1">
                Please confirm that your organisation has the rights to use the template content you are about to author.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5 px-6 pb-6">
          {stale && (
            <div className="flex items-start gap-2 rounded-md border border-amber-400/40 bg-amber-400/10 p-3 text-sm text-amber-600">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              The terms have been updated. Please re-read the new text before accepting.
            </div>
          )}

          {/* Full attestation text — shown in full (no inner scroll), readable before accepting. */}
          <div className="rounded-md border border-border-subtle bg-bg-base p-4 text-sm text-text-secondary leading-relaxed whitespace-pre-wrap font-mono">
            {loadingText ? (
              <div className="space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-4/5" />
              </div>
            ) : (
              textVersion?.body_text ?? 'Loading terms…'
            )}
          </div>

          {/* Source description */}
          <div className="space-y-1.5">
            <Label htmlFor="source-desc" className="text-sm">
              Content origin <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="source-desc"
              placeholder="e.g. 'Original questions authored by our security team' or 'Adapted from our internal vendor assessment process'"
              value={sourceDescription}
              onChange={(e) => setSourceDescription(e.target.value)}
              rows={3}
              disabled={submitting}
            />
            <p className="text-xs text-text-muted">
              Briefly describe where your template content comes from. Recorded as part of the attestation.
            </p>
          </div>

          {/* Acceptance checkbox (immediate — no scroll-gate) */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="ip-accept"
              checked={accepted}
              onCheckedChange={(v) => setAccepted(Boolean(v))}
              disabled={loadingText || submitting}
            />
            <Label htmlFor="ip-accept" className="text-sm text-text-primary cursor-pointer leading-relaxed">
              I confirm that my organisation has the right to use this content and I have authority to make
              this attestation on its behalf.
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* One confirm → the atomic create + record. Cancel writes nothing. */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/templates')} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={!canSubmit}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create Template &amp; Record Attestation
        </Button>
      </div>
    </div>
  );
}
